import React, { useRef, useState } from 'react'
import { InsuranceProviderInterface } from '../../types/provider-types'
import { providerPermissionFields } from './providersData'
import clsx from 'clsx'
import ProviderIconSelect from './ProviderIconSelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faEllipsisV, faTrash } from '@fortawesome/free-solid-svg-icons'
import { Menu } from '@headlessui/react'
import { useDeleteModel } from '../../store/use-delete-model'
import { toast } from 'react-toastify'
import { ParseErrors } from '../../utils/ParseErrors'
import axiosInstance from '../../utils/axiosInstance'
import DeleteModel from '../../models/delete-model'
import { useProviderEditModel } from '../../store/use-provider-edit-model'
import ProviderChaseModel from '../../models/provider-edit-model'
import GetUser from '../../utils/getUser'
import DropdownPanel from '../DropdownPanel'

interface IProviderRow {
    row: InsuranceProviderInterface
}

const ProviderRow: React.FC<IProviderRow> = ({ row }) => {
    const currentUser = GetUser()
    const [item, setItem] = useState<InsuranceProviderInterface | undefined>(row)
    const [loading, setLoading] = useState<boolean>(false)
    const menuButtonRef = useRef<HTMLButtonElement | null>(null)
    const { onOpenDeletePopup, onCloseDeletePopup, initialValues } = useDeleteModel()
    const { initialValues: editInitialValues, onOpen } = useProviderEditModel()


    // ------------------------------ [ HANDLE DELETE PROVIDER ] ---------------------------------------

    const HandleDeleteProvider = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.delete(`/provider/${item?.id}`)
            toast.success(res.data?.message)
            setItem(undefined)
            onCloseDeletePopup()
            setLoading(false)
        } catch (error) {
            toast.error(ParseErrors(error))
            onCloseDeletePopup()
            setLoading(false)
        }
    }


    // -------------------------------------- [ UPDATE ITEM ] -----------------------------

    const UpdateItemData = (data: Record<string, any>) => {
        setItem((prev) => ({ ...prev, ...data }))
    }


    if (!item) {
        return null
    }

    return (
        <>
            <tr className="group border-b border-edge-faint last:border-b-0 hover:bg-overlay-hover transition-colors duration-200">
                {/* --------------------------- [ INSURER ] ------------------- */}
                <td className="sticky left-0 z-[1] bg-surface group-hover:bg-[linear-gradient(var(--color-bg-overlay-hover),var(--color-bg-overlay-hover))] py-3 pl-1 pr-4 align-middle">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-[14px] font-semibold text-text-primary leading-tight truncate" title={item?.name}>
                            {item?.name}
                        </p>
                        {item?.type && <span className={clsx(
                            "shrink-0 h-[18px] px-1.5 flex items-center rounded-full text-[10px] font-semibold whitespace-nowrap",
                            item.type === "Renewals" ? "bg-amber-500/10 text-amber-700/80 dark:text-amber-300/80" : "bg-sky-500/10 text-sky-700/80 dark:text-sky-300/80"
                        )}>
                            {item.type}
                        </span>}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                        {item?.schemeCode && <span className="h-5 px-1.5 flex items-center rounded-md bg-input-bg font-code text-[10px] font-medium text-text-primary">
                            {item.schemeCode}
                        </span>}
                        {[item?.productType, item?.product].filter(Boolean).map((tag, index) => (
                            <span key={index} className="h-5 px-1.5 flex items-center rounded-md border border-edge text-[10px] font-medium text-light-gray whitespace-nowrap">
                                {tag}
                            </span>
                        ))}
                    </div>
                </td>

                {/* --------------------------- [ DOCUMENT RULES ] ------------------- */}
                {providerPermissionFields.map((field) => (
                    <td key={field.id} className="py-3 px-1 align-middle">
                        <ProviderIconSelect
                            providerId={item?.id}
                            field={field}
                            value={item[field.id]}
                            lnSubtag={item?.lnSubtag || []}
                            mmSubtag={item?.mmSubtag || []}
                            UpdateItemData={UpdateItemData}
                        />
                    </td>
                ))}

                {/* --------------------------- [ ACTIONS ] ------------------- */}
                <td className="py-3 pr-1 align-middle">
                    {currentUser?.type === "super-admin" && <Menu as="div" className="relative flex justify-end">
                        {({ open }) => (
                            <>
                                <Menu.Button
                                    ref={menuButtonRef}
                                    aria-label="Insurer actions"
                                    title="More actions"
                                    className={clsx("h-8 w-8 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200", open && "bg-overlay text-text-primary")}
                                >
                                    <FontAwesomeIcon icon={faEllipsisV} className="text-[14px]" />
                                </Menu.Button>

                                <DropdownPanel open={open} anchorRef={menuButtonRef} align="right">
                                    <Menu.Items static className="min-w-[160px] p-1 rounded-xl bg-surface-elevated border border-edge shadow-overlay focus:outline-none">
                                        <Menu.Item>
                                            <button
                                                type="button"
                                                onClick={() => onOpen({ id: item?.id, data: item })}
                                                className="w-full flex gap-2.5 px-3 py-2 rounded-lg text-[13px] text-text-primary items-center font-medium hover:bg-overlay-hover transition-colors duration-200"
                                            >
                                                <FontAwesomeIcon icon={faEdit} className="text-[12px]" />
                                                Edit
                                            </button>
                                        </Menu.Item>
                                        <Menu.Item>
                                            <button
                                                type="button"
                                                onClick={() => onOpenDeletePopup({
                                                    id: item?.id,
                                                    onClick: HandleDeleteProvider,
                                                    scope: "provider",
                                                    title: "Delete this insurer?",
                                                    description: "The insurer is removed from the list and can no longer be selected on new chases.",
                                                    detail: item?.name,
                                                    confirmLabel: "Delete insurer",
                                                })}
                                                className="w-full flex gap-2.5 px-3 py-2 rounded-lg text-[13px] items-center font-medium text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors duration-200"
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="text-[12px]" />
                                                Delete
                                            </button>
                                        </Menu.Item>
                                    </Menu.Items>
                                </DropdownPanel>
                            </>
                        )}
                    </Menu>}
                </td>
            </tr>
            {((initialValues?.id === item?.id && initialValues?.scope === "provider") || editInitialValues?.id === item?.id) && <tr>
                <td>
                    {initialValues?.id === item?.id && initialValues?.scope === "provider" && <DeleteModel loading={loading} />}
                    {editInitialValues?.id === item?.id && <ProviderChaseModel UpdateItemData={UpdateItemData} />}
                </td>
            </tr>}
        </>
    )
}

export default ProviderRow
